import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  message,
  Modal,
  Row,
  Col,
  Tooltip,
  Spin,
  Typography,
  Tag,
  Alert,
  Pagination,
  Table,
  Tabs,
  Divider,
} from "antd";
import type { TableProps } from "antd";
import "remixicon/fonts/remixicon.css";
import { useGlobal } from "../contexts/GlobalContext";
import { getOpenId } from "../utils/auth";
import request from "../utils/request";
import { getErrorText } from "../utils/errorText";

const { Text, Paragraph } = Typography;

interface LoginRecord {
  ip: string;
  user_agent: string;
  time: string;
  status: boolean;
}

interface LoginLogResponse {
  records: LoginRecord[];
  total: number;
  page: number;
  page_size: number;
}

interface PasskeyInfo {
  id: number;
  created_at: string;
  last_used: string;
  credential: string;
}

interface OAuthBindings {
  qq_bind: boolean;
  github_bind: boolean;
}

interface DeletionStatus {
  state: string;
  requestedAt?: string;
  softDeleteAt?: string;
  hardDeleteAt?: string;
  reason?: string;
}

interface APISecretStatus {
  exists: boolean;
  legacy: boolean;
  needsRotation: boolean;
  keyPrefix?: string;
  scopes: string[];
  lastUsedAt?: string;
  lastSuccess?: boolean;
  lastSource?: string;
  lastErrorId?: string;
}

const UserSettings: React.FC = () => {
  const { token, API_BASE_URL } = useGlobal();
  const { user } = useGlobal();
  const [emailForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [usernameForm] = Form.useForm();
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
  const [isChangePasswordModalVisible, setIsChangePasswordModalVisible] =
    useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentAction, setCurrentAction] = useState<"email" | "password">(
    "email"
  );
  const [sendingCode, setSendingCode] = useState(false);
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [passwordResetStep, setPasswordResetStep] = useState<
    "verify" | "reset"
  >("verify");
  const [verifyForm] = Form.useForm();
  const [passwordCountdown, setPasswordCountdown] = useState(0);
  const [sendingPasswordCode, setSendingPasswordCode] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [unbinding, setUnbinding] = useState(false);

  const [passkeys, setPasskeys] = useState<PasskeyInfo[]>([]);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [oauthBindings, setOauthBindings] = useState<OAuthBindings>({
    qq_bind: false,
    github_bind: false,
  });
  const [oauthBindingsLoading, setOauthBindingsLoading] = useState(false);
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [deletionStatus, setDeletionStatus] = useState<DeletionStatus | null>(null);
  const [deletionLoading, setDeletionLoading] = useState(false);
  const [deletionReason, setDeletionReason] = useState("");
  const [apiSecretStatus, setApiSecretStatus] = useState<APISecretStatus>();
  const [apiSecretLoading, setApiSecretLoading] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const openId = getOpenId(token);

  const [loginLogs, setLoginLogs] = useState<LoginRecord[]>([]);
  const [loginLogsTotal, setLoginLogsTotal] = useState(0);
  const [loginLogsPage, setLoginLogsPage] = useState(1);
  const [loginLogsPageSize, setLoginLogsPageSize] = useState(20);
  const [loginLogsLoading, setLoginLogsLoading] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [countdown]);

  // 密码重置验证码倒计时
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (passwordCountdown > 0) {
      timer = setTimeout(() => {
        setPasswordCountdown(passwordCountdown - 1);
      }, 1000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [passwordCountdown]);

  const handleEmailChange = () => {
    setIsEmailModalVisible(true);
  };

  const fetchPasskeys = useCallback(async () => {
    if (!token) return;

    setPasskeyLoading(true);
    try {
      const response = await request.get<{
        code: number;
        msg: string;
        data: PasskeyInfo[];
      }>(`${API_BASE_URL}/api/v3/user/passkeys`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        setPasskeys(response.data.data);
      } else {
        message.error(response.data.msg || "获取通行密钥列表失败");
      }
    } catch (error) {
      console.error("获取通行密钥列表失败:", error);
      message.error("获取通行密钥列表失败，请稍后重试");
    } finally {
      setPasskeyLoading(false);
    }
  }, [API_BASE_URL, token]);

  const fetchOAuthBindings = useCallback(async () => {
    if (!token) return;

    setOauthBindingsLoading(true);
    try {
      const response = await request.get<{
        code: number;
        msg: string;
        data: OAuthBindings;
      }>(`${API_BASE_URL}/api/v3/user/oauth-bindings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        setOauthBindings(response.data.data || { qq_bind: false, github_bind: false });
      } else {
        message.error(response.data.msg || "获取社交账号绑定状态失败");
      }
    } catch (error) {
      console.error("获取社交账号绑定状态失败:", error);
      message.error("获取社交账号绑定状态失败，请稍后重试");
    } finally {
      setOauthBindingsLoading(false);
    }
  }, [API_BASE_URL, token]);

  const fetchAPISecret = useCallback(async () => {
    if (!token) return;
    try {
      const response = await request.get(`${API_BASE_URL}/api/v3/user/api-secret`);
      if (response.data?.code === 200) setApiSecretStatus(response.data.data);
    } catch (error) {
      console.error("获取 API Secret 状态失败", error);
    }
  }, [API_BASE_URL, token]);

  const resetAPISecret = () => {
    Modal.confirm({
      title: apiSecretStatus?.exists ? "重置全局 API Secret" : "创建全局 API Secret",
      content: "操作完成后所有旧 Secret 会立即失效。新 Secret 只展示一次，请立即保存。",
      okText: apiSecretStatus?.exists ? "确认重置" : "确认创建",
      okButtonProps: { danger: Boolean(apiSecretStatus?.exists) },
      onOk: async () => {
        setApiSecretLoading(true);
        try {
          const response = await request.post(`${API_BASE_URL}/api/v3/user/api-secret/reset`, { confirm: true });
          if (response.data?.code !== 200) throw Object.assign(new Error(response.data?.msg || "重置失败"), { response });
          const secret = String(response.data.data?.secret || "");
          Modal.success({
            title: "API Secret 已生成",
            width: 620,
            content: (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Alert type="warning" showIcon message="完整 Secret 只显示这一次" />
                <Input.TextArea value={secret} readOnly autoSize={{ minRows: 2, maxRows: 3 }} />
                <Button onClick={() => { void navigator.clipboard.writeText(secret); message.success("已复制"); }}>复制 Secret</Button>
              </Space>
            ),
          });
          await fetchAPISecret();
        } catch (error) {
          message.error(getErrorText(error, "API Secret 重置失败"));
        } finally {
          setApiSecretLoading(false);
        }
      },
    });
  };

  // 添加注册新Passkey的函数
  const registerNewPasskey = async () => {
    if (!token) return;

    if (passkeys.length >= 3) {
      message.warning("最多只能添加3个通行密钥");
      return;
    }

    try {
      setRegistering(true);

      // 开始注册流程
      const beginResponse = await request.post<{
        code: number;
        msg: string;
        data: any; // 注册选项
      }>(`${API_BASE_URL}/api/v3/auth/passkey/register/begin`, null, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (beginResponse.data.code !== 200) {
        throw new Error(beginResponse.data.msg || "开始通行密钥注册失败");
      }

      const publicKey = beginResponse.data.data.publicKey;

      // 准备WebAuthn选项
      const publicKeyCredentialCreationOptions = {
        challenge: base64UrlDecode(publicKey.challenge),
        rp: {
          name: publicKey.rp.name,
          id: publicKey.rp.id,
        },
        user: {
          id: base64UrlDecode(publicKey.user.id),
          name: publicKey.user.name,
          displayName: publicKey.user.displayName,
        },
        pubKeyCredParams: publicKey.pubKeyCredParams,
        timeout: publicKey.timeout,
        excludeCredentials: publicKey.excludeCredentials
          ? publicKey.excludeCredentials.map((cred: any) => ({
              id: base64UrlDecode(cred.id),
              type: cred.type,
            }))
          : [],
        authenticatorSelection: publicKey.authenticatorSelection,
        attestation: publicKey.attestation,
      };

      // 调用浏览器的WebAuthn API创建凭据
      const credential = (await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions as any,
      })) as PublicKeyCredential;

      // 准备发送到服务器的数据
      const attestationResponse =
        credential.response as AuthenticatorAttestationResponse;
      const attestation = {
        id: credential.id,
        rawId: base64UrlEncode(new Uint8Array(credential.rawId)),
        type: credential.type,
        response: {
          attestationObject: base64UrlEncode(
            new Uint8Array(attestationResponse.attestationObject)
          ),
          clientDataJSON: base64UrlEncode(
            new Uint8Array(attestationResponse.clientDataJSON)
          ),
        },
      };

      // 完成注册
      const finishResponse = await request.post<{
        code: number;
        msg: string;
      }>(`${API_BASE_URL}/api/v3/auth/passkey/register/finish`, attestation, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (finishResponse.data.code === 200) {
        message.success("通行密钥注册成功");
        // 刷新Passkey列表
        fetchPasskeys();
      } else {
        throw new Error(finishResponse.data.msg || "通行密钥注册失败");
      }
    } catch (error: any) {
      if (error.name === "NotAllowedError") {
        message.error("操作被取消或没有权限");
      } else if (error.name === "SecurityError") {
        message.error("安全错误，可能是跨域问题");
      } else if (error.name === "AbortError") {
        message.error("操作被中止");
      } else {
        message.error(error.message || "通行密钥注册失败");
      }
      console.error("通行密钥注册错误:", error);
    } finally {
      setRegistering(false);
    }
  };

  // 删除Passkey
  const deletePasskey = async (id: number) => {
    if (!token) return;

    try {
      const response = await request.delete<{
        code: number;
        msg: string;
      }>(`${API_BASE_URL}/api/v3/user/passkeys/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        message.success("删除成功");
        // 刷新Passkey列表
        fetchPasskeys();
      } else {
        message.error(response.data.msg || "删除失败");
      }
    } catch (error) {
      console.error("删除通行密钥失败:", error);
      message.error("删除失败，请稍后重试");
    }
  };

  // 在useEffect中添加初始化Passkey列表
  useEffect(() => {
    if (token) {
      fetchPasskeys();
      fetchOAuthBindings();
      fetchAPISecret();
    }
  }, [token, fetchPasskeys, fetchOAuthBindings, fetchAPISecret]);

  useEffect(() => {
    usernameForm.setFieldsValue({
      username: (user?.apiInfo as any)?.username || "",
    });
  }, [user?.apiInfo, usernameForm]);

  const fetchDeletionStatus = useCallback(async () => {
    if (!token) return;
    try {
      const response = await request.get<{
        code: number;
        msg: string;
        data: DeletionStatus;
      }>(`${API_BASE_URL}/api/v3/user/deletion`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.code === 200) {
        setDeletionStatus(response.data.data);
      }
    } catch (error) {
      console.error("获取注销状态失败:", error);
    }
  }, [API_BASE_URL, token]);

  useEffect(() => {
    fetchDeletionStatus();
  }, [fetchDeletionStatus]);

  const checkUsername = async () => {
    const username = usernameForm.getFieldValue("username");
    if (!username) {
      message.warning("请先输入用户名");
      return;
    }
    setUsernameChecking(true);
    try {
      const response = await request.get(`${API_BASE_URL}/api/v3/user/username/check`, {
        params: { username },
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data;
      if (data.code === 200 && data.data?.available) {
        message.success("用户名可用");
      } else {
        message.warning(data.data?.reason || "用户名不可用");
      }
    } catch (error) {
      message.error("检查用户名失败，请稍后重试");
    } finally {
      setUsernameChecking(false);
    }
  };

  const handleUsernameUpdate = async (values: { username: string }) => {
    setUsernameSaving(true);
    try {
      const response = await request.put(
        `${API_BASE_URL}/api/v3/user/username`,
        { username: values.username },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.code === 200) {
        message.success("用户名已更新");
        window.location.reload();
      } else {
        message.error(response.data.msg || "用户名更新失败");
      }
    } catch (error: any) {
      message.error(error?.response?.data?.msg || "用户名更新失败，请稍后重试");
    } finally {
      setUsernameSaving(false);
    }
  };

  const handleRequestDeletion = () => {
    // 注销需已绑定邮箱：归档数据以邮件形式交付。
    if (!user?.apiInfo?.email) {
      Modal.warning({
        title: "请先绑定邮箱",
        content: "账号注销时会把归档数据以邮件形式发送给你，因此需要先绑定邮箱后再申请注销。",
      });
      return;
    }
    Modal.confirm({
      title: "确认申请注销账号",
      content: "账号会进入冷静期，期间可取消；具体完成时间以提交后的状态页为准。",
      okText: "申请注销",
      cancelText: "取消",
      okButtonProps: { danger: true },
      onOk: async () => {
        setDeletionLoading(true);
        try {
          const response = await request.post(
            `${API_BASE_URL}/api/v3/user/deletion`,
            { reason: deletionReason },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.data.code === 200) {
            setDeletionStatus(response.data.data);
            setDeletionReason("");
            message.success("已进入账号注销冷静期");
          } else {
            message.error(response.data.msg || "申请注销失败");
          }
        } catch (error: any) {
          message.error(error?.response?.data?.msg || "申请注销失败，请稍后重试");
        } finally {
          setDeletionLoading(false);
        }
      },
    });
  };

  const handleCancelDeletion = async () => {
    setDeletionLoading(true);
    try {
      const response = await request.delete(`${API_BASE_URL}/api/v3/user/deletion`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.code === 200) {
        message.success("已取消账号注销");
        fetchDeletionStatus();
      } else {
        message.error(response.data.msg || "取消注销失败");
      }
    } catch (error: any) {
      message.error(error?.response?.data?.msg || "取消注销失败，请稍后重试");
    } finally {
      setDeletionLoading(false);
    }
  };

  const base64UrlDecode = (base64Url: string): ArrayBuffer => {
    const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
    const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");

    const rawData = window.atob(base64);
    const buffer = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; i++) {
      buffer[i] = rawData.charCodeAt(i);
    }

    return buffer.buffer;
  };

  const base64UrlEncode = (arrayBuffer: Uint8Array): string => {
    const base64 = window.btoa(
      String.fromCharCode.apply(null, Array.from(new Uint8Array(arrayBuffer)))
    );
    return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
  };

  // 检测浏览器是否支持Passkey
  const isPasskeySupported =
    typeof window !== "undefined" &&
    window.PublicKeyCredential !== undefined &&
    typeof window.PublicKeyCredential === "function";

  // 获取登录日志数据
  const fetchLoginLogs = useCallback(
    async (page: number, pageSize: number) => {
      if (!token) return;

      setLoginLogsLoading(true);
      try {
        const response = await request.get<{
          code: number;
          msg: string;
          data: LoginLogResponse;
        }>(`${API_BASE_URL}/api/v3/user/login-logs`, {
          params: {
            page,
            page_size: pageSize,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.code === 200) {
          setLoginLogs(response.data.data.records);
          setLoginLogsTotal(response.data.data.total);
          setLoginLogsPage(response.data.data.page);
          setLoginLogsPageSize(response.data.data.page_size);
        } else {
          message.error(response.data.msg || "获取登录日志失败");
        }
      } catch (error) {
        message.error("获取登录日志失败，请稍后重试");
      } finally {
        setLoginLogsLoading(false);
      }
    },
    [API_BASE_URL, token]
  );

  // 组件挂载后获取登录日志
  useEffect(() => {
    if (token) {
      fetchLoginLogs(loginLogsPage, loginLogsPageSize);
    }
  }, [token, fetchLoginLogs, loginLogsPage, loginLogsPageSize]);

  // 处理登录日志分页变化
  const handleLoginLogsPageChange = (page: number, pageSize: number) => {
    fetchLoginLogs(page, pageSize);
  };

  // 登录日志表格列配置
  const loginLogsColumns: TableProps<LoginRecord>["columns"] = [
    {
      title: "登录时间",
      dataIndex: "time",
      key: "time",
      render: (text: string) => new Date(text).toLocaleString("zh-CN"),
      width: "25%",
    },
    {
      title: "登录IP",
      dataIndex: "ip",
      key: "ip",
      width: "20%",
    },
    {
      title: "登录设备",
      dataIndex: "user_agent",
      key: "user_agent",
      width: "40%",
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: "15%",
      render: (status: boolean) => (
        <Tag color={status ? "success" : "error"}>
          {status ? "成功" : "失败"}
        </Tag>
      ),
    },
  ];

  const handlePasswordChange = () => {
    setIsChangePasswordModalVisible(true);
    setPasswordResetStep("verify");
  };

  const handlePasswordVerify = async (_values: any) => {
    // 验证密码 Modal 当前未启用(无入口触发);保留以备后续二次确认流程接入后端校验。
    setIsPasswordModalVisible(false);

    if (currentAction === "email") {
      setIsEmailModalVisible(true);
    } else {
      setIsChangePasswordModalVisible(true);
    }
  };

  const sendVerificationCode = async () => {
    const newEmail = emailForm.getFieldValue("newEmail");
    if (!newEmail) {
      message.error("请先输入新邮箱");
      return;
    }

    setSendingCode(true);
    setCountdown(60);

    try {
      const response = await request.post(`${API_BASE_URL}/api/v3/user/email/verify-code`, {
        email: newEmail,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data;

      if (data.code === 200) {
        message.success("验证码已发送至新邮箱");
      } else if (data.code === 409) {
        message.error("该邮箱已被注册");
        // 如果发送失败，重置倒计时
        setCountdown(0);
      } else {
        message.error(data.msg || "发送验证码失败");
        // 如果发送失败，重置倒计时
        setCountdown(0);
      }
    } catch (error) {
      message.error("发送验证码失败，请稍后重试");
      // 如果发送失败，重置倒计时
      setCountdown(0);
    } finally {
      setSendingCode(false);
    }
  };

  const handleEmailUpdate = async (values: any) => {
    if (!values.verificationCode) {
      message.error("请输入验证码");
      return;
    }

    setUpdatingEmail(true);
    try {
      const response = await request.put(`${API_BASE_URL}/api/v3/user/email`, {
        old_email: user?.apiInfo?.email,
        new_email: values.newEmail,
        code: values.verificationCode,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data;

      if (data.code === 200) {
        message.success("邮箱更新成功");
        setIsEmailModalVisible(false);
        // 刷新页面以更新信息
        window.location.reload();
      } else {
        message.error(data.msg || "邮箱更新失败");
      }
    } catch (error) {
      message.error("邮箱更新失败，请稍后重试");
    } finally {
      setUpdatingEmail(false);
    }
  };

  // 发送密码重置验证码
  const sendPasswordVerificationCode = async () => {
    if (!user?.apiInfo?.email) {
      message.error("未找到邮箱信息");
      return;
    }

    setSendingPasswordCode(true);
    setPasswordCountdown(60);

    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/auth/password/reset-code`,
        { email: user.apiInfo.email },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = response.data;

      if (data.code === 200) {
        message.success("验证码已发送至邮箱");
      } else {
        message.error(data.msg || "发送验证码失败");
        setPasswordCountdown(0);
      }
    } catch (error) {
      message.error("发送验证码失败，请稍后重试");
      setPasswordCountdown(0);
    } finally {
      setSendingPasswordCode(false);
    }
  };

  // 验证码验证通过
  const handleVerifySuccess = () => {
    setPasswordResetStep("reset");
  };

  // 验证码验证通过后重置密码
  const handlePasswordReset = async (values: any) => {
    setResettingPassword(true);
    try {
      const response = await request.post(`${API_BASE_URL}/api/v3/auth/password/reset`, {
        email: user?.apiInfo?.email,
        password: values.newPassword,
        code: values.verificationCode,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data;

      if (data.code === 200) {
        message.success("密码重置成功");
        setIsChangePasswordModalVisible(false);
        passwordForm.resetFields();
        verifyForm.resetFields();
        setPasswordResetStep("verify");
        // 刷新页面以更新信息
        window.location.reload();
      } else {
        message.error(data.msg || "密码重置失败");
      }
    } catch (error) {
      message.error("密码重置失败，请稍后重试");
    } finally {
      setResettingPassword(false);
    }
  };

  // 解绑社交账户
  const handleUnbind = async (type: "qq" | "github") => {
    Modal.confirm({
      title: "确认取消绑定",
      content: `确定要取消绑定${
        type === "qq" ? "QQ" : "Github"
      }账号吗？取消绑定后将无法使用该账号登录。`,
      okText: "确认取消",
      cancelText: "取消",
      okButtonProps: { danger: true },
      onOk: async () => {
        setUnbinding(true);
        try {
          const response = await request.post(
            `${API_BASE_URL}/api/v3/user/oauth/unbind`,
            { provider: type },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const data = response.data;

          if (data.code === 200) {
            message.success("解绑成功");
            fetchOAuthBindings();
          } else {
            message.error(data.msg || "解绑失败");
          }
        } catch (error) {
          message.error("解绑失败，请稍后重试");
        } finally {
          setUnbinding(false);
        }
      },
    });
  };

  // 添加绑定处理函数
  const handleGithubBind = () => {
    const clientId = "Ov23liGj9NmbjEwJMfPH";
    const redirectUri = encodeURIComponent(
      "https://ai.zhheo.com/console/login/github"
    );
    const scope = "user:email";
    const githubAuthUrl = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}`;

    window.location.href = githubAuthUrl;
  };

  const handleQQBind = () => {
    const clientId = "102689824";
    const redirectUri = encodeURIComponent(
      "https://ai.zhheo.com/console/login/qq"
    );
    const scope = "get_unionid";
    const qqAuthUrl = `https://graph.qq.com/oauth2.0/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}`;

    window.location.href = qqAuthUrl;
  };

  if (user === null) {
    return <Spin />;
  }

  return (
    <div
      style={{
        background: "#f0f2f5",
        minHeight: "100%",
        borderRadius: 8,
        padding: "16px",
      }}
    >
      <Card
        title={
          <span>
            <i
              className="ri-user-settings-line"
              style={{ marginRight: "8px", color: "#1677ff" }}
            ></i>
            用户设置
          </span>
        }
        variant="outlined"
        styles={{
          body: { padding: 8 },
        }}
      >
        {/* U10 设置页 Tab 化(05§4.12):账户 / 安全 / 登录记录 */}
        <Tabs
          defaultActiveKey="account"
          style={{ padding: "0 8px" }}
          items={[
            {
              key: "account",
              label: "账户",
              children: (
                <>
        <div style={{ padding: "0 16px" }}>
          {deletionStatus?.state === "deletion_pending" && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="账号处于注销冷静期"
              description={`预计软注销时间：${
                deletionStatus.softDeleteAt
                  ? new Date(deletionStatus.softDeleteAt).toLocaleString("zh-CN")
                  : "-"
              }，冷静期内仍可取消注销。`}
            />
          )}
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                <td style={{ padding: "12px 0", width: "120px", color: "#666" }}>
                  <Space>
                    <i className="ri-user-3-line" style={{ color: "#1677ff" }} />
                    <Text strong>UID</Text>
                  </Space>
                </td>
                <td>{user?.apiInfo?.open_id || user?.uid || "-"}</td>
                <td style={{ width: "100px" }}></td>
              </tr>
              <tr>
                <td style={{ padding: "12px 0", width: "120px", color: "#666" }}>
                  <Space>
                    <i className="ri-at-line" style={{ color: "#1677ff" }} />
                    <Text strong>用户名</Text>
                  </Space>
                </td>
                <td colSpan={2}>
                  <Form
                    form={usernameForm}
                    layout="inline"
                    onFinish={handleUsernameUpdate}
                    style={{ rowGap: 8 }}
                  >
                    <Form.Item
                      name="username"
                      style={{ flex: 1, minWidth: 220, marginBottom: 0 }}
                      rules={[
                        { required: true, message: "请输入用户名" },
                        { min: 3, message: "用户名至少3位" },
                        { max: 32, message: "用户名最多32位" },
                        {
                          pattern: /^[a-z0-9_-]+$/,
                          message: "仅支持小写字母、数字、下划线和连字符",
                        },
                      ]}
                    >
                      <Input placeholder="设置唯一用户名" />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }}>
                      <Space>
                        <Button onClick={checkUsername} loading={usernameChecking}>
                          检查
                        </Button>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={usernameSaving}
                        >
                          保存
                        </Button>
                      </Space>
                    </Form.Item>
                  </Form>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px 0", width: "120px", color: "#666" }}>
                  <Space>
                    <i className="ri-mail-line" style={{ color: "#1677ff" }} />
                    <Text strong>邮箱</Text>
                  </Space>
                </td>
                <td>{user?.apiInfo?.email || "-"}</td>
                <td style={{ textAlign: "right" }}>
                  <Button size="small" onClick={handleEmailChange}>
                    修改
                  </Button>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px 0", width: "120px", color: "#666" }}>
                  <Space>
                    <i className="ri-lock-line" style={{ color: "#1677ff" }} />
                    <Text strong>密码</Text>
                  </Space>
                </td>
                <td>********</td>
                <td style={{ textAlign: "right" }}>
                  <Button size="small" onClick={handlePasswordChange}>
                    修改
                  </Button>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "12px 0", width: "120px", color: "#666" }}>
                  <Space>
                    <i className="ri-key-line" style={{ color: "#1677ff" }} />
                    <Text strong>全局 API Secret</Text>
                    <Tooltip title="用于兼容老 V2 账户级接口。项目 API Key 和子 Key 仍在项目中独立管理。">
                      <i
                        className="ri-information-line"
                        style={{ color: "#1677ff", cursor: "pointer" }}
                      />
                    </Tooltip>
                  </Space>
                </td>

                <td>
                  <Space direction="vertical" size={2}>
                    <Space wrap>
                      <Text code>{apiSecretStatus?.keyPrefix ? `${apiSecretStatus.keyPrefix}...` : "未创建"}</Text>
                      {apiSecretStatus?.legacy && <Tag color="orange">旧明文存储，建议重置</Tag>}
                      {apiSecretStatus?.exists && !apiSecretStatus.legacy && <Tag color="green">已哈希存储</Tag>}
                    </Space>
                    <Text type="secondary">权限：{apiSecretStatus?.scopes?.join(", ") || "legacy:v2"}</Text>
                    {apiSecretStatus?.lastUsedAt && (
                      <Text type="secondary">
                        上次调用：{new Date(apiSecretStatus.lastUsedAt).toLocaleString("zh-CN")}
                        {apiSecretStatus.lastSource ? ` · ${apiSecretStatus.lastSource}` : ""}
                        {apiSecretStatus.lastSuccess === false ? " · 失败" : " · 成功"}
                      </Text>
                    )}
                  </Space>
                </td>
                <td style={{ textAlign: "right" }}>
                  <Button danger={Boolean(apiSecretStatus?.exists)} size="small" loading={apiSecretLoading} onClick={resetAPISecret}>
                    {apiSecretStatus?.exists ? "重置" : "创建"}
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Card
              title={
                <Space>
                  <i className="ri-user-unfollow-line" style={{ color: "#ff4d4f" }} />
                  注销账号
                </Space>
              }
              variant="outlined"
              size="small"
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Space wrap>
                  <Text type="secondary">当前状态</Text>
                  <Tag color={deletionStatus?.state === "deletion_pending" ? "orange" : "green"}>
                    {deletionStatus?.state === "deletion_pending" ? "冷静期中" : "正常"}
                  </Tag>
                </Space>
                {deletionStatus?.state === "deletion_pending" ? (
                  <>
                    <Text type="secondary">
                      硬删除时间：
                      {deletionStatus.hardDeleteAt
                        ? new Date(deletionStatus.hardDeleteAt).toLocaleString("zh-CN")
                        : "-"}
                    </Text>
                    <Button onClick={handleCancelDeletion} loading={deletionLoading}>
                      取消注销
                    </Button>
                  </>
                ) : (
                  <>
                    <Input.TextArea
                      rows={2}
                      value={deletionReason}
                      onChange={(event) => setDeletionReason(event.target.value)}
                      placeholder="可选：注销原因"
                    />
                    <Button
                      danger
                      onClick={handleRequestDeletion}
                      loading={deletionLoading}
                    >
                      申请注销
                    </Button>
                  </>
                )}
              </Space>
            </Card>
          </Col>
        </Row>
        <Card
          title={
            <Space>
              <i
                className="ri-links-line"
                style={{
                  marginRight: "6px",
                  fontSize: "18px",
                  color: "#1677ff",
                }}
              />
              社交账户绑定
            </Space>
          }
          variant="outlined"
          styles={{
            body: { padding: 8 },
          }}
        >
          <div style={{ padding: "0 16px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <td style={{ padding: "12px 0", width: "120px", color: "#666" }}>
                    <Space>
                      <i className="ri-qq-line" style={{ color: "#12B7F5" }} />
                      <Text strong>QQ账号</Text>
                    </Space>
                  </td>
                  <td>
                    {oauthBindingsLoading
                      ? "读取中"
                      : oauthBindings.qq_bind
                      ? "已绑定"
                      : "未绑定"}
                  </td>
                  <td style={{ width: "100px", textAlign: "right" }}>
                    {oauthBindings.qq_bind ? (
                      <Button
                        danger
                        size="small"
                        onClick={() => handleUnbind("qq")}
                        loading={unbinding}
                      >
                        取消绑定
                      </Button>
                    ) : (
                      <Button type="primary" size="small" onClick={handleQQBind}>
                        绑定
                      </Button>
                    )}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: "12px 0", width: "120px", color: "#666" }}>
                    <Space>
                      <i className="ri-github-line" style={{ color: "#333" }} />
                      <Text strong>Github账号</Text>
                    </Space>
                  </td>
                  <td>
                    {oauthBindingsLoading
                      ? "读取中"
                      : oauthBindings.github_bind
                      ? "已绑定"
                      : "未绑定"}
                  </td>
                  <td style={{ width: "100px", textAlign: "right" }}>
                    {oauthBindings.github_bind ? (
                      <Button
                        danger
                        size="small"
                        onClick={() => handleUnbind("github")}
                        loading={unbinding}
                      >
                        取消绑定
                      </Button>
                    ) : (
                      <Button type="primary" size="small" onClick={handleGithubBind}>
                        绑定
                      </Button>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
                </>
              ),
            },
            {
              key: "security",
              label: "安全",
              children: (
                <>
        {!isPasskeySupported && (
          <Paragraph type="secondary" style={{ padding: "8px 16px" }}>
            当前浏览器不支持通行密钥;密码与邮箱修改在「账户」Tab。
          </Paragraph>
        )}
        {isPasskeySupported && (
          <Card
            title={
              <Space>
                <i
                  className="ri-fingerprint-line"
                  style={{
                    marginRight: "6px",
                    fontSize: "18px",
                    color: "#1677ff",
                  }}
                />
                通行密钥 管理
              </Space>
            }
            variant="outlined"
            style={{ marginTop: 16 }}
          >
            <div>
              <Paragraph>
                通行密钥可以让您无需输入密码即可安全登录账户。您可以使用指纹、面容识别或设备PIN码进行认证。
                <Text type="secondary">
                  {" "}
                  (最多可添加3个通行密钥，当前已添加 {passkeys.length} 个)
                </Text>
              </Paragraph>

              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Button
                    type="primary"
                    icon={<i className="ri-add-line" />}
                    onClick={registerNewPasskey}
                    loading={registering}
                    disabled={!isPasskeySupported}
                  >
                    注册新通行密钥
                    {passkeys.length >= 3 && " (已达上限)"}
                  </Button>
                </Col>
              </Row>

              <Divider />

              <Table
                dataSource={passkeys}
                rowKey="id"
                loading={passkeyLoading}
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "创建时间",
                    dataIndex: "created_at",
                    key: "created_at",
                    render: (text: string) =>
                      new Date(text).toLocaleString("zh-CN"),
                  },
                  {
                    title: "最后使用",
                    dataIndex: "last_used",
                    key: "last_used",
                    render: (text: string) =>
                      new Date(text).toLocaleString("zh-CN"),
                  },
                  {
                    title: "操作",
                    key: "action",
                    render: (_, record: PasskeyInfo) => (
                      <Button
                        type="link"
                        danger
                        style={{ padding: 0 }}
                        onClick={() => {
                          Modal.confirm({
                            title: "确认删除",
                            content:
                              "删除此通行密钥后，您将无法再使用它登录。确定要删除吗？",
                            onOk: () => deletePasskey(record.id),
                          });
                        }}
                      >
                        删除
                      </Button>
                    ),
                  },
                ]}
                locale={{ emptyText: "暂无通行密钥" }}
              />
            </div>
          </Card>
        )}
                </>
              ),
            },
            {
              key: "logs",
              label: "登录记录",
              children: (
                <>
        <Card
          title={
            <Space>
              <i
                className="ri-history-line"
                style={{
                  marginRight: "6px",
                  fontSize: "18px",
                  color: "#1677ff",
                }}
              />
              登录日志
            </Space>
          }
          variant="outlined"
          style={{ marginTop: "16px" }}
          extra={
            <Button
              size="small"
              icon={
                <i className="ri-refresh-line" style={{ marginRight: 4 }} />
              }
              onClick={() => fetchLoginLogs(loginLogsPage, loginLogsPageSize)}
              loading={loginLogsLoading}
            >
              刷新
            </Button>
          }
        >
          <Table
            columns={loginLogsColumns}
            dataSource={loginLogs}
            rowKey={(record) => `${record.ip}-${record.time}`}
            loading={loginLogsLoading}
            pagination={false}
            size="small"
            locale={{
              emptyText: "暂无登录记录",
            }}
          />
          <Row justify="end" style={{ marginTop: "16px" }}>
            <Pagination
              current={loginLogsPage}
              total={loginLogsTotal}
              pageSize={loginLogsPageSize}
              onChange={handleLoginLogsPageChange}
              showSizeChanger
              showQuickJumper
              showTotal={(total) => `共 ${total} 条记录`}
            />
          </Row>
        </Card>
                </>
              ),
            },
          ]}
        />
      </Card>

      {/* 密码验证弹窗 */}
      <Modal
        title="验证密码"
        open={isPasswordModalVisible}
        onCancel={() => setIsPasswordModalVisible(false)}
        footer={null}
      >
        <Form onFinish={handlePasswordVerify} layout="vertical">
          <Form.Item
            label="请输入当前密码"
            name="password"
            rules={[{ required: true, message: "请输入密码" }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              确认
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* 修改邮箱弹窗 */}
      <Modal
        title="修改邮箱"
        open={isEmailModalVisible}
        onCancel={() => setIsEmailModalVisible(false)}
        footer={null}
      >
        <Form form={emailForm} onFinish={handleEmailUpdate} layout="vertical">
          <Form.Item
            label="新邮箱"
            name="newEmail"
            rules={[
              { required: true, message: "请输入新邮箱" },
              { type: "email", message: "请输入有效的邮箱地址" },
            ]}
          >
            <Space.Compact style={{ width: "100%" }}>
              <Input placeholder="请输入新邮箱" />
              <Button
                onClick={sendVerificationCode}
                loading={sendingCode}
                disabled={sendingCode || countdown > 0}
              >
                {sendingCode
                  ? "发送中..."
                  : countdown > 0
                  ? `${countdown}秒后重试`
                  : "发送验证码"}
              </Button>
            </Space.Compact>
          </Form.Item>

          <Form.Item
            label="验证码"
            name="verificationCode"
            rules={[{ required: true, message: "请输入验证码" }]}
          >
            <Input placeholder="请输入验证码" />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={updatingEmail}
              disabled={updatingEmail}
            >
              {updatingEmail ? "更新中..." : "确认修改"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* 修改密码弹窗 */}
      <Modal
        title="修改密码"
        open={isChangePasswordModalVisible}
        onCancel={() => {
          setIsChangePasswordModalVisible(false);
          setPasswordResetStep("verify");
          passwordForm.resetFields();
          verifyForm.resetFields();
        }}
        footer={null}
      >
        {passwordResetStep === "verify" ? (
          <Form
            form={verifyForm}
            onFinish={handleVerifySuccess}
            layout="vertical"
          >
            <Form.Item
              label="验证码"
              extra={`验证码将发送至邮箱：${user?.apiInfo?.email || "-"}`}
            >
              <Space.Compact style={{ width: "100%" }}>
                <Form.Item
                  name="verificationCode"
                  noStyle
                  rules={[{ required: true, message: "请输入验证码" }]}
                >
                  <Input placeholder="请输入验证码" />
                </Form.Item>
                <Button
                  onClick={sendPasswordVerificationCode}
                  loading={sendingPasswordCode}
                  disabled={sendingPasswordCode || passwordCountdown > 0}
                >
                  {sendingPasswordCode
                    ? "发送中..."
                    : passwordCountdown > 0
                    ? `${passwordCountdown}秒后重试`
                    : "发送验证码"}
                </Button>
              </Space.Compact>
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                下一步
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <Form
            form={passwordForm}
            onFinish={handlePasswordReset}
            layout="vertical"
          >
            <Form.Item
              label="新密码"
              name="newPassword"
              rules={[
                { required: true, message: "请输入新密码" },
                { min: 6, message: "密码长度不能小于6位" },
              ]}
            >
              <Input.Password placeholder="请输入新密码" />
            </Form.Item>

            <Form.Item
              label="确认新密码"
              name="confirmPassword"
              dependencies={["newPassword"]}
              rules={[
                { required: true, message: "请确认新密码" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("newPassword") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("两次输入的密码不一致"));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="请再次输入新密码" />
            </Form.Item>

            <Form.Item
              name="verificationCode"
              initialValue={verifyForm.getFieldValue("verificationCode")}
              hidden
            />

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={resettingPassword}
                disabled={resettingPassword}
              >
                {resettingPassword ? "重置中..." : "确认重置"}
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default UserSettings;
